import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Brain, Dumbbell, Heart, Sparkles, Activity, Zap, CheckCircle2 } from "lucide-react";

const AIAnalysisLoader = ({ isActive, onComplete, apiDone }) => {
  const { t } = useTranslation();

  const STAGES = [
    { key: "mood", icon: Heart, labelKey: "training.generating.stageMood", duration: 1800, color: "#f472b6" },
    { key: "cognitive", icon: Brain, labelKey: "training.generating.stageCognitive", duration: 2200, color: "#818cf8" },
    { key: "physical", icon: Dumbbell, labelKey: "training.generating.stagePhysical", duration: 2000, color: "#22d3ee" },
    { key: "stress", icon: Activity, labelKey: "training.generating.stageStress", duration: 1600, color: "#34d399" },
    { key: "building", icon: Sparkles, labelKey: "training.generating.stageBuilding", duration: 2400, color: "#fbbf24" },
    { key: "optimizing", icon: Zap, labelKey: "training.generating.stageOptimizing", duration: 1500, color: "#0ea5e9" },
  ];

  const [activeStage, setActiveStage] = useState(0);
  const [stageProgress, setStageProgress] = useState(0);
  const [completedStages, setCompletedStages] = useState([]);
  const [particles, setParticles] = useState([]);
  const [showReady, setShowReady] = useState(false);
  const apiDoneRef = useRef(false);
  const intervalRef = useRef(null);
  const stageTimerRef = useRef(null);

  // Track apiDone changes
  useEffect(() => {
    apiDoneRef.current = apiDone;
  }, [apiDone]);

  // Generate floating particles
  useEffect(() => {
    if (!isActive) return;
    const pts = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 2 + Math.random() * 4,
      delay: Math.random() * 5,
      duration: 3 + Math.random() * 4,
    }));
    setParticles(pts);
  }, [isActive]);

  // Stage progression
  useEffect(() => {
    if (!isActive) return;

    const advanceStage = (stageIdx) => {
      if (stageIdx >= STAGES.length) {
        // All visual stages done — wait for API if needed
        const waitForApi = () => {
          if (apiDoneRef.current) {
            setShowReady(true);
            setTimeout(() => onComplete?.(), 1200);
          } else {
            setTimeout(waitForApi, 200);
          }
        };
        waitForApi();
        return;
      }

      setActiveStage(stageIdx);
      setStageProgress(0);

      const stage = STAGES[stageIdx];
      const tickInterval = 50;
      const totalTicks = stage.duration / tickInterval;
      let tick = 0;

      intervalRef.current = setInterval(() => {
        tick++;
        setStageProgress(Math.min((tick / totalTicks) * 100, 100));

        if (tick >= totalTicks) {
          clearInterval(intervalRef.current);
          setCompletedStages(prev => [...prev, stageIdx]);
          // Small pause then next stage
          stageTimerRef.current = setTimeout(() => advanceStage(stageIdx + 1), 300);
        }
      }, tickInterval);
    };

    // Start first stage after entrance animation
    const startTimer = setTimeout(() => advanceStage(0), 600);

    return () => {
      clearTimeout(startTimer);
      clearInterval(intervalRef.current);
      clearTimeout(stageTimerRef.current);
    };
  }, [isActive, onComplete]);

  if (!isActive) return null;

  const totalProgress = ((completedStages.length + (stageProgress / 100)) / STAGES.length) * 100;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden" data-testid="ai-analysis-loader">
      {/* Animated background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-sky-950 to-indigo-950" />

      {/* Floating particles */}
      {particles.map(p => (
        <div
          key={p.id}
          className="absolute rounded-full opacity-20"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            background: STAGES[activeStage]?.color || "#0ea5e9",
            animation: `float-particle ${p.duration}s ease-in-out infinite`,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}

      {/* Radial glow behind center */}
      <div
        className="absolute w-96 h-96 rounded-full blur-3xl opacity-20 transition-colors duration-1000"
        style={{ background: STAGES[activeStage]?.color || "#0ea5e9" }}
      />

      {/* Main content */}
      <div className="relative z-10 max-w-lg w-full mx-4 text-center">
        {/* Central pulsing icon */}
        <div className="mb-10 flex justify-center">
          <div className="relative">
            <div
              className="w-24 h-24 rounded-2xl flex items-center justify-center transition-all duration-700"
              style={{
                background: `linear-gradient(135deg, ${STAGES[activeStage]?.color || "#0ea5e9"}33, ${STAGES[activeStage]?.color || "#0ea5e9"}11)`,
                boxShadow: `0 0 60px ${STAGES[activeStage]?.color || "#0ea5e9"}33`,
              }}
            >
              {showReady ? (
                <CheckCircle2 className="w-12 h-12 text-emerald-400 animate-scale-in" strokeWidth={1.5} />
              ) : (
                (() => {
                  const Icon = STAGES[activeStage]?.icon || Sparkles;
                  return <Icon className="w-12 h-12 text-white animate-pulse-gentle" strokeWidth={1.5} />;
                })()
              )}
            </div>
            {/* Orbiting ring */}
            {!showReady && (
              <div className="absolute inset-[-12px] rounded-2xl border border-white/10 animate-spin-slow" />
            )}
          </div>
        </div>

        {/* Status text */}
        <div className="mb-8">
          {showReady ? (
            <h2 className="text-2xl font-light text-emerald-400 animate-fade-in" style={{ fontFamily: "Outfit" }}>
              {t('training.generating.planReady')}
            </h2>
          ) : (
            <h2
              key={activeStage}
              className="text-xl sm:text-2xl font-light text-white/90 animate-text-in"
              style={{ fontFamily: "Outfit" }}
            >
              {STAGES[activeStage]?.labelKey ? t(STAGES[activeStage].labelKey) : ""}
            </h2>
          )}
        </div>

        {/* Main progress bar */}
        <div className="mb-8 px-4">
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-300 ease-out"
              style={{
                width: `${showReady ? 100 : totalProgress}%`,
                background: `linear-gradient(90deg, ${STAGES[Math.max(0, activeStage - 1)]?.color || "#0ea5e9"}, ${STAGES[activeStage]?.color || "#0ea5e9"})`,
                boxShadow: `0 0 12px ${STAGES[activeStage]?.color || "#0ea5e9"}66`,
              }}
            />
          </div>
          <p className="text-xs text-white/30 mt-2 font-mono">
            {showReady ? "100" : Math.round(totalProgress)}%
          </p>
        </div>

        {/* Stage checklist */}
        <div className="space-y-2 px-4 text-left">
          {STAGES.map((stage, idx) => {
            const Icon = stage.icon;
            const isCompleted = completedStages.includes(idx);
            const isCurrent = idx === activeStage && !showReady;
            const isPending = idx > activeStage;

            return (
              <div
                key={stage.key}
                className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-500 ${
                  isCurrent ? "bg-white/5" : isCompleted ? "bg-white/[0.02]" : ""
                }`}
                style={{ opacity: isPending ? 0.25 : 1 }}
              >
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                  isCompleted ? "bg-emerald-500/20" : isCurrent ? "bg-white/10" : "bg-white/5"
                }`}>
                  {isCompleted ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" strokeWidth={2} />
                  ) : (
                    <Icon className={`w-4 h-4 ${isCurrent ? "text-white" : "text-white/30"}`} strokeWidth={1.5} />
                  )}
                </div>
                <span className={`text-sm transition-all duration-500 ${
                  isCompleted ? "text-emerald-400/80" : isCurrent ? "text-white/90" : "text-white/25"
                }`}>
                  {stage.labelKey ? t(stage.labelKey) : stage.key}
                </span>
                {isCurrent && (
                  <div className="ml-auto flex gap-0.5">
                    <span className="w-1 h-1 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1 h-1 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1 h-1 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes float-particle {
          0%, 100% { transform: translateY(0) translateX(0); opacity: 0.15; }
          25% { transform: translateY(-30px) translateX(10px); opacity: 0.3; }
          50% { transform: translateY(-10px) translateX(-15px); opacity: 0.1; }
          75% { transform: translateY(-40px) translateX(5px); opacity: 0.25; }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow { animation: spin-slow 8s linear infinite; }
        @keyframes pulse-gentle {
          0%, 100% { opacity: 0.8; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }
        .animate-pulse-gentle { animation: pulse-gentle 2s ease-in-out infinite; }
        @keyframes text-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-text-in { animation: text-in 0.5s ease-out; }
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.5); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-scale-in { animation: scale-in 0.5s ease-out; }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in { animation: fade-in 0.5s ease-out; }
      `}</style>
    </div>,
    document.body
  );
};

export default AIAnalysisLoader;
